import Highlight from '@src/components/common/Highlight';
import { Tag, Tooltip } from 'antd';
import moment from 'moment';
import React from 'react';
import { getFilteredAlertsByCategory, SEVERITY_CONFIG, STATIC_SIDEBAR_FILTERS } from '../utils';
import RootCauseTitle from './RootCauseTitle';
import { isAppModule } from '@utils/constants';

export const getFilteredAlerts = ({ alerts, filters }) => {
  const { ALL_ALERTS, CRITICAL, WARNING, INFO, VIDEO_ALERT, AD_ALERT, ACTIVE } =
    STATIC_SIDEBAR_FILTERS;

  if (filters.includes(ALL_ALERTS)) {
    return alerts;
  }

  const selectedSeverities = filters.filter((f) => [CRITICAL, WARNING, INFO].includes(f));
  const selectedType = filters.filter((f) => [VIDEO_ALERT, AD_ALERT].includes(f));
  const selectedMetrics = filters.filter(
    (f) => ![...selectedSeverities, ...selectedType, ACTIVE].includes(f),
  );
  const filterSeverityAlerts = getFilteredAlertsByCategory({ alerts, filters: selectedSeverities });
  const filterActiveAlerts = filters.includes(ACTIVE)
    ? getFilteredAlertsByCategory({ alerts: filterSeverityAlerts, filters: [ACTIVE] })
    : filterSeverityAlerts;
  const filterProductAlerts = getFilteredAlertsByCategory({
    alerts: filterActiveAlerts,
    filters: selectedType,
  });
  const filterMetricAlerts = getFilteredAlertsByCategory({
    alerts: filterProductAlerts,
    filters: selectedMetrics,
  });

  return filterMetricAlerts;
};

export const getSecsFromDuration = (duration = '') => {
  duration = (duration || '').trim();
  let res = 0;
  if (duration?.split(' ')?.length !== 3) {
    return res;
  }
  const [h, m, s] = duration.split(' ');
  res += +h?.split('h')?.[0] * 3600;
  res += +m?.split('m')?.[0] * 60;
  res += +s?.split('s')?.[0];
  return res;
};

export const getColumns = (
  searchText,
  isAlertPartnerAccount,
  showImpactedUniques,
  sortBy,
  isPaginationEnabled,
) => {
  // TODO: Replace this with isAppModule check in 3.74 release
  if (isPaginationEnabled) {
    return getDPIColumns(searchText, showImpactedUniques, sortBy);
  }
  return getDefaultColumns(searchText, isAlertPartnerAccount, showImpactedUniques);
};

// TODO: showImpactedUniques logic should be removed once NWR metrics are replaced with new metrics in AI Alert 2.0
export const getDPIColumns = (searchText, showImpactedUniques, sortBy) => [
  {
    title: <span style={{ marginLeft: '5px' }}>&nbsp;&nbsp;Alert ID</span>,
    dataIndex: 'alertId',
    key: 'alertId',
    columnKey: 'alertId',
    width: 120,
    sorter: true,
    sortOrder: sortBy.key === 'alertId' ? sortBy.order : null,
    render: (alertId) => <Highlight text={alertId?.toString() || ''} highlight={searchText} />,
  },
  {
    title: 'Root Cause Leads',
    dataIndex: 'rootCauseDisplay',
    key: 'rootCauseDisplay',
    columnKey: 'rootCauseDisplay',
    width: 330,
    sorter: true,
    sortOrder: sortBy.key === 'rootCauseDisplay' ? sortBy.order : null,
    render: (_, record) => <RootCauseTitle record={record} />,
  },
  {
    title: 'Metric',
    dataIndex: 'metricDisplayName',
    key: 'metricDisplayName',
    columnKey: 'metricDisplayName',
    width: 250,
    sorter: true,
    sortOrder: sortBy.key === 'metricDisplayName' ? sortBy.order : null,
    render: (metricDisplayName, record) =>
      record.isDeletedMetric ? (
        <Tooltip title="This metric has been deleted.">{metricDisplayName}</Tooltip>
      ) : (
        <>
          <Highlight text={metricDisplayName} highlight={searchText} />
          {record.kpiName ? (
            <Tag color="purple" style={{ marginLeft: 6 }}>
              {record.kpiName}
            </Tag>
          ) : null}
        </>
      ),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    columnKey: 'status',
    width: 90,
    sorter: false, // can be included but dont see the need as we have only 2 possible status, Ended and Active and have a filter for Active
    sortOrder: null,
  },
  {
    title: 'Peak/Minimum Value',
    dataIndex: 'peakValueDisplay',
    key: 'peakValueDisplay',
    width: 110,
    sorter: false,
    sortOrder: null,
  },

  {
    title: 'Peak Severity',
    dataIndex: 'peakSeverity',
    key: 'peakSeverity',
    columnKey: 'peakSeverity',
    width: 120,
    sorter: true,
    sortOrder: sortBy.key === 'peakSeverity' ? sortBy.order : null,
    render: (severity) => {
      return (
        <>
          <span style={{ marginRight: '3px' }}>{SEVERITY_CONFIG[severity].icon}</span>
          {SEVERITY_CONFIG[severity].label}
        </>
      );
    },
  },
  ...(showImpactedUniques
    ? [
        {
          title: 'Impacted Uniques',
          dataIndex: 'cumulativeImpactedUniquesDisplay',
          key: 'cumulativeImpactedUniquesDisplay',
          width: 140,
          sorter: false,
        },
      ]
    : []),
  {
    title: 'Impacted Count',
    dataIndex: 'impactedCountDisplay',
    render: (count, record) => {
      return count !== 'NA' ? `${count} (${record.impactedCountPercentage}%)` : 'NA';
    },
    key: 'impactedCountDisplay',
    width: 140,
    sorter: false,
  },
  {
    title: 'Time Alert Fired',
    dataIndex: 'eventTime',
    key: 'eventTime',
    columnKey: 'eventTime',
    width: 160,
    sorter: true,
    sortOrder: sortBy.key === 'eventTime' ? sortBy.order : null,
  },
  {
    title: 'Duration',
    dataIndex: 'duration',
    key: 'duration',
    width: 110,
    sorter: false,
  },
];

export const getDefaultColumns = (searchText, isAlertPartnerAccount, showImpactedUniques) => [
  {
    title: <span style={{ marginLeft: '5px' }}>&nbsp;&nbsp;Alert ID</span>,
    dataIndex: 'alertId',
    key: 'alertId',
    width: 120,
    sorter: (a, b) => a.alertId - b.alertId,
    render: (alertId) => <Highlight text={alertId?.toString() || ''} highlight={searchText} />,
  },
  {
    title: 'Root Cause Leads',
    dataIndex: 'rootCauseDisplay',
    key: 'rootCauseDisplay',
    width: 330,
    render: (_, record) => <RootCauseTitle record={record} />,
    sorter: (a, b) =>
      (a.rootCauseDisplay || '')
        .toLowerCase()
        .localeCompare((b.rootCauseDisplay || '').toLowerCase()),
  },
  {
    title: 'Metric',
    dataIndex: 'metricDisplayName',
    key: 'metricDisplayName',
    width: 250,
    render: (metricDisplayName, record) =>
      record.isDeletedMetric ? (
        <Tooltip title="This metric has been deleted.">{metricDisplayName}</Tooltip>
      ) : (
        <>
          <Highlight text={metricDisplayName} highlight={searchText} />
          {record.kpiName ? (
            <Tag color="purple" style={{ marginLeft: 6 }}>
              {record.kpiName}
            </Tag>
          ) : null}
        </>
      ),
    sorter: (a, b) =>
      (a.metricDisplayName || '')
        .toLowerCase()
        .localeCompare((b.metricDisplayName || '').toLowerCase()),
  },
  // only used in video/ad
  ...(isAlertPartnerAccount
    ? [
        {
          title: 'Account',
          width: 150,
          dataIndex: 'accountName',
          key: 'accountName',
          sorter: (a, b) =>
            (a.accountName || '').toLowerCase().localeCompare((b.accountName || '').toLowerCase()),
        },
        {
          title: 'Customer',
          width: 150,
          dataIndex: 'c3Name',
          key: 'c3Name',
          sorter: (a, b) =>
            (a.c3Name || '').toLowerCase().localeCompare((b.c3Name || '').toLowerCase()),
        },
      ]
    : []),
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 90,
    sorter: (a, b) => a.hasEnded - b.hasEnded,
  },
  {
    title: isAppModule ? 'Peak/Minimum Value' : 'Peak Value',
    dataIndex: 'peakValueDisplay',
    key: 'peakValueDisplay',
    width: 110,
    sorter: (a, b) => a.peakValue - b.peakValue,
  },

  {
    title: 'Peak Severity',
    dataIndex: 'peakSeverity',
    key: 'peakSeverity',
    width: 120,
    render: (severity) => (
      <>
        <span style={{ marginRight: '3px' }}>{SEVERITY_CONFIG[severity].icon}</span>
        {SEVERITY_CONFIG[severity].label}
      </>
    ),
    sorter: (a, b) => SEVERITY_CONFIG[a.peakSeverity].order - SEVERITY_CONFIG[b.peakSeverity].order,
  },
  ...(showImpactedUniques
    ? [
        {
          title: 'Impacted Uniques',
          dataIndex: 'cumulativeImpactedUniquesDisplay',
          key: 'cumulativeImpactedUniquesDisplay',
          width: 140,
          sorter: (a, b) => a.cumulativeImpactedUniques - b.cumulativeImpactedUniques,
        },
      ]
    : []),
  {
    title: 'Time Alert Fired',
    dataIndex: 'eventTime',
    key: 'eventTime',
    columnKey: 'eventTime',
    width: 160,
    defaultSortOrder: 'descend',
    sorter: (a, b) => (moment(a.eventTimestamp).isBefore(moment(b.eventTimestamp)) ? -1 : 1),
  },
  {
    title: 'Duration',
    dataIndex: 'duration',
    key: 'duration',
    width: 110,
    sorter: (a, b) => {
      const firstItem = getSecsFromDuration(a.duration);
      const secondItem = getSecsFromDuration(b.duration);
      if (firstItem > secondItem) {
        return 1;
      } else if (firstItem === secondItem) {
        return 0;
      }
      return -1;
    },
  },
];

const exportConfig = [
  {
    title: 'Alert Id',
    dataKey: 'alertId',
  },
  {
    title: 'Root Cause',
    dataKey: 'rootCauseDisplay',
  },
  {
    title: 'Metric',
    dataKey: 'metricDisplayName',
  },
  {
    title: 'Account',
    dataKey: 'accountName',
  },
  {
    title: 'Customer',
    dataKey: 'c3Name',
  },
  {
    title: 'Status',
    dataKey: 'status',
  },
  {
    title: 'Peak Value',
    dataKey: 'peakValueDisplay',
  },
  {
    title: 'Peak Severity',
    dataKey: 'peakSeverity',
  },
  {
    title: 'Impacted Uniques',
    dataKey: 'cumulativeImpactedUniquesDisplay',
  },
  // { title: 'Impacted Count', dataKey: 'impactedCountDisplay' },
  {
    title: 'Time Alert Fired',
    dataKey: 'eventTime',
  },
  {
    title: 'Duration',
    dataKey: 'duration',
  },
];

export const getExportColumns = (isAlertPartnerAccount, showImpactedUniques) => {
  if (!isAlertPartnerAccount) {
    return exportConfig.filter((column) => !['c3Name', 'accountName'].includes(column.dataKey));
  }
  if (!showImpactedUniques) {
    return exportConfig.filter((column) => column.dataKey != 'cumulativeImpactedUniquesDisplay');
  }
  return exportConfig;
};
